import { envDomain, nginxAddress, selfEndpoint } from '@/constant/env';

import { Logger } from '../Logging';
import { TIMEOUT_DURATIONS } from './constants';
import { Experiment } from './types';

export const getVariantPathByKey = (
  experiment: Experiment | undefined,
  variantKey: string
): string | undefined => {
  if (!experiment?.variants) return undefined;
  if (!Array.isArray(experiment.variants)) return undefined;

  const variant = experiment.variants.find(
    (variant) => variant.variantKey === variantKey
  );

  return variant?.path;
};

const nginxToEnvDomainEndpoint = (redirectUrl: string): string => {
  return redirectUrl.replace(nginxAddress, envDomain);
};
/**
 * Checks if the redirect URL is valid by pinging it.
 * If it fails, it will fallback to the home page.
 *
 * Deliberately avoiding showing original page, since those pages are likely to either be 404s or
 * contain info we no longer want to show
 */
export async function getSafeRedirectPathWithFallback(
  redirectToPath: string
): Promise<string> {
  const logger = new Logger('getSafeRedirectPathWithFallback');

  const fallbackPath = envDomain;

  if (!redirectToPath) {
    logger.warn({
      message: 'No redirect URL provided, falling back to home path',
      context: { fallbackPath },
    });

    return fallbackPath;
  }

  const isAbsoluteUrl =
    redirectToPath.startsWith('https://') ||
    redirectToPath.startsWith('http://');

  const domain = selfEndpoint;
  const cleanPath = redirectToPath.replace(/^\/+/, '');
  const fullRedirectUrl: string = isAbsoluteUrl
    ? redirectToPath
    : `${domain}/${cleanPath}`;

  let finalRedirectURL: string = fullRedirectUrl;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIMEOUT_DURATIONS.REDIRECT_PATH_TIMEOUT
    );

    const pingRedirectPath = await fetch(fullRedirectUrl, {
      method: 'HEAD',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (pingRedirectPath.ok) {
      logger.log({
        message:
          'Redirect URL is valid, setting final redirect URL to: ' +
          fullRedirectUrl,
        context: {
          fullRedirectUrl,
          pingRedirectPath,
          fallbackPath,
        },
      });

      finalRedirectURL = fullRedirectUrl;
    } else {
      logger.warn({
        message:
          'Redirect URL is invalid, falling back to home path: ' + fallbackPath,
        context: {
          fullRedirectUrl,
          status: pingRedirectPath.status,
          fallbackPath,
        },
      });

      finalRedirectURL = fallbackPath;
    }
  } catch (error) {
    logger.warn({
      message:
        'Error pinging redirect URL, falling back to home path: ' +
        fallbackPath,
      context: {
        fullRedirectUrl,
        error: error instanceof Error ? error.message : String(error),
        fallbackPath,
      },
    });

    finalRedirectURL = fallbackPath;
  }

  const result = nginxToEnvDomainEndpoint(finalRedirectURL);
  logger.log({
    message: 'Final Redirect URL is: ' + result,
    context: {
      finalRedirectURL: result,
    },
  });

  return result;
}
